import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { extend } from '@thd-nucleus/data-sources';
import { number, string, func, bool } from 'prop-types';
import { shipping } from '@thd-olt-component-react/delivery-options';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { CheckAvailability } from '@thd-olt-component-react/check-availability';
import { dataModel } from './dataModel';
import { Header } from './Header/Header';
import { DualPath } from './DualPath/DualPath';
import { NotifyMe } from './NotifyMe/NotifyMe';
import { LiveGoods } from './LiveGoods/LiveGoods';
import { useFulfillment } from '../hooks/useFulfillment';
import { Discontinued } from './Discontinued/Discontinued';
import {
  getLiveGoodsStock,
  setDETAZip,
  getDETAZip,
  setDeliveryZip,
  getDeliveryZip
} from './helper/utils';

import './fulfillment.scss';

const Fulfillment = (props) => {
  const {
    itemId,
    storeId,
    quantity,
    covidAlert,
    dualPath,
    onChange,
    onDeliveryZipCodeChange,
    bodfsMinimumThreshold,
    bopisMinimumThreshold,
    bopisMinimumThresholdStores,
    bopisHolidayTiming
  } = props;

  const {
    channel,
  } = useContext(ExperienceContext);
  const { storeId: defaultStoreId, storeName, storeZip } = useStore();
  const detaZip = getDETAZip();
  const deliveryZip = getDeliveryZip();
  const [zipCode, setZipCode] = useState(deliveryZip || detaZip || storeZip);

  const [shippingModel, setShippingModel] = useState(null);
  const [shippingServiceModel, setShippingServiceModel] = useState(null);
  const [isDeliveryOptionChanged, changeDeliveryOption] = useState(false);

  const [selectedFulfillment, setFulfillmentSelection] = useState('');

  const updateZipCode = (newZipCode) => {
    if (newZipCode) {
      setDeliveryZip(newZipCode);
      setDETAZip(newZipCode);
      setZipCode(newZipCode);
    }
  };

  const onChangeOfZipCode = (data) => {
    const updatedModel = shipping(data?.shipping);
    updateZipCode(data?.shipping?.zipCode);
    setShippingModel(updatedModel);
  };

  useEffect(() => {
    if (shippingModel) {
      changeDeliveryOption(true);
      if (shippingModel?.shipModes?.length > 0) {
        setShippingServiceModel(shippingModel);
      }
      if (onDeliveryZipCodeChange) {
        onDeliveryZipCodeChange(shippingModel);
      }
    }
  }, [onDeliveryZipCodeChange, shippingModel, zipCode]);

  const [{
    type,
    fulfillment,
    identifiers,
    value,
    availabilityType,
    fulfillmentModels,
    currentFulfillment
  }, changeFulfillment] = useFulfillment({
    itemId,
    covidAlert,
    quantity,
    zipCode,
    storeId: storeId || defaultStoreId,
    storeName,
    channel,
    shippingServiceModel,
    changeDeliveryOption,
    isDeliveryOptionChanged,
    onChange,
    bodfsMinimumThreshold,
    bopisMinimumThreshold,
    bopisMinimumThresholdStores,
    bopisHolidayTiming,
    selectedFulfillment,
    setFulfillmentSelection
  });

  const [storeObj = {}, shippingObj = {}, expressObj = {}] = fulfillmentModels;

  if (availabilityType?.discontinued) {
    return (
      <Discontinued />
    );
  }
  if (getLiveGoodsStock(fulfillment) === 0) {
    return (
      <LiveGoods
        storeName={storeName}
        seasonStatusEligible={fulfillment?.seasonStatusEligible}
      />
    );
  }

  if (dualPath) {
    return (
      <DualPath />
    );
  }
  if (((storeObj.unavailable && shippingObj.unavailable) || (shippingObj.oos && type === 'Online'))) {
    return (
      <NotifyMe
        itemId={itemId}
        store={storeObj}
        shipping={shippingObj}
      />
    );
  }

  return (
    <>
      {value ? (
        <Header
          productType={identifiers.productType}
          itemId={itemId}
          zipCode={zipCode}
          value={value}
          onZipCodeChange={onChangeOfZipCode}
          quantity={parseInt(quantity, 10)}
        />
      )
        : null}
      <div className="fulfillment__content-wrapper card-deck">
        {fulfillmentModels.map((fulfillmentModel, i) => {
          const wrapperClasses = classNames({
            card: true,
            u__legal: channel === 'mobile',
            'mobile--margin-right': true,
            'card-container': true,
            'u__text--light': !fulfillmentModel.enabled,
            'card-selected': fulfillmentModel.method === currentFulfillment?.method,
            'card-unselected': fulfillmentModel.method !== currentFulfillment?.method,
            'card-enabled': fulfillmentModel.enabled
          });
          return (
            // eslint-disable-next-line
            <a
              key={i}
              type="button"
              className={wrapperClasses}
              onClick={() => {
                if ((fulfillmentModel.method === currentFulfillment?.method) || !fulfillmentModel.enabled) return;
                changeFulfillment(fulfillmentModel);
              }}
            >
              {fulfillmentModel.template}
            </a>
          );
        })}
      </div>
      {currentFulfillment?.subTemplate && !currentFulfillment?.unavailable && (
        <div className="fulfillment__fullcontent">
          {currentFulfillment.subTemplate}
        </div>
      )}
    </>
  );
};

Fulfillment.displayName = 'Fulfillment';

Fulfillment.propTypes = {
  /** An Item that has product highlights */
  itemId: string.isRequired,
  storeId: string,
  covidAlert: bool,
  quantity: number,
  onChange: func,
  dualPath: bool,
  onDeliveryZipCodeChange: func,
  bodfsMinimumThreshold: number,
  bopisMinimumThreshold: number,
  bopisMinimumThresholdStores: string,
  bopisHolidayTiming: number
};

Fulfillment.defaultProps = {
  quantity: 1,
  storeId: null,
  covidAlert: false,
  onChange: () => {},
  onDeliveryZipCodeChange: () => {},
  dualPath: false,
  bodfsMinimumThreshold: null,
  bopisMinimumThreshold: null,
  bopisMinimumThresholdStores: null,
  bopisHolidayTiming: null
};

// ['identifiers', 'info', 'pricing', 'availabilityType', 'fulfillment']
Fulfillment.dataModel = extend(
  CheckAvailability,
  dataModel
);

export { Fulfillment };
